import React, { useState, useEffect, useMemo } from 'react';
import { X, ChevronDown } from 'react-feather';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { athleteAPI } from '../services/api';
import { useResponsive } from '../hooks/useResponsive';
import Modal from './Modal';
import { Select, TextInput } from './ui';
import {
  rangeError,
  hasNoErrors,
  RANGE_HEIGHT_FEET,
  RANGE_HEIGHT_INCHES,
  RANGE_WEIGHT_LBS,
} from '../utils/validation';

/* The form repeated one label style, one sub-label style and one box style
 * across every field. None of them sets `outline`: inline, it outranks the
 * global :focus-visible ring in globals.css, which is why nothing in this modal
 * drew a keyboard focus indicator — the onFocus/onBlur border swaps that stood
 * in for one only ever fired, and only painted a border, for a mouse. */
const athleticLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '14px',
  fontWeight: '600',
  color: '#374151',
  marginBottom: '8px',
};

const athleticSubLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '12px',
  fontWeight: '500',
  color: '#6B7280',
  marginBottom: '8px',
};

const athleticControlStyle: React.CSSProperties = {
  padding: '12px 14px',
  border: '1px solid #D1D5DB',
  borderRadius: '8px',
  fontSize: '15px',
};

/* Same box, but the right padding stays clear of the chevron <Select> draws for
 * itself. Passing the text-box padding straight to a Select overrides the
 * primitive's own 38px gutter and runs a long option name under the arrow. */
const athleticSelectStyle: React.CSSProperties = {
  ...athleticControlStyle,
  padding: '12px 38px 12px 14px',
};

const athleticErrorStyle: React.CSSProperties = {
  display: 'block',
  marginTop: '6px',
  fontSize: '13px',
  color: '#DC2626',
};

interface AthleticInfoEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialData: {
    id: string;
    sport: string;
    position: string;
    height_inches?: number;
    weight_lbs?: number;
    bats?: string;
    throws?: string;
    awards?: string[];
  };
  onUpdate: (updatedData: any) => void;
  /** Render inside the unified ProfileEditModal (no own overlay). */
  embedded?: boolean;
  /** Called after a successful save. Falls back to onClose. */
  onSaved?: () => void;
}

const AthleticInfoEditModal: React.FC<AthleticInfoEditModalProps> = ({
  isOpen,
  onClose,
  initialData,
  onUpdate,
  embedded = false,
  onSaved
}) => {
  const { sessionToken } = useAuth();
  const toast = useToast();
  const { isMobile } = useResponsive();
  const [isSaving, setIsSaving] = useState(false);

  // Form state
  const [sport, setSport] = useState('');
  const [selectedPositions, setSelectedPositions] = useState<string[]>([]);
  const [availablePositions, setAvailablePositions] = useState<string[]>([]);
  const [showPositionPicker, setShowPositionPicker] = useState(false);
  // #203 — snapshot of the positions when the picker opens, so Cancel can
  // restore the prior selection instead of keeping the live toggles.
  const [positionsSnapshot, setPositionsSnapshot] = useState<string[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [heightFeet, setHeightFeet] = useState('');
  const [heightInches, setHeightInches] = useState('');
  const [weight, setWeight] = useState('');
  const [bats, setBats] = useState('');
  const [throws, setThrows] = useState('');
  // Awards moved to AwardsAndHonorsEditModal — but the sport-info POST
  // payload still includes them, so we keep a passthrough copy of whatever
  // the initial data carried and resend it on save. Prevents this modal
  // from clobbering awards that were set elsewhere.
  const [awards, setAwards] = useState<string[]>([]);

  // Load positions from API
  useEffect(() => {
    const loadPositions = async () => {
      if (!sessionToken) return;
      try {
        // TODO(audit #203): sport-keyed position list — this endpoint only
        // returns softball positions, so non-softball athletes see the wrong
        // list. No sport-keyed positions API exists in this file yet.
        const response = await athleteAPI.getSoftballPositions(sessionToken);
        setAvailablePositions(response.positions || []);
      } catch (error) {
        console.error('Error loading positions:', error);
        // Fallback positions if API fails
        setAvailablePositions([
          'Pitcher', 'Catcher', 'First Base', 'Second Base', 'Third Base',
          'Shortstop', 'Left Field', 'Center Field', 'Right Field', 'Utility'
        ]);
      }
    };
    if (isOpen) {
      loadPositions();
    }
  }, [isOpen, sessionToken]);

  // Initialize form with existing data
  useEffect(() => {
    if (isOpen && initialData) {
      setSport(initialData.sport || '');
      // Parse position string into array
      const positionsArray = initialData.position 
        ? initialData.position.split(',').map(p => p.trim()).filter(p => p.length > 0)
        : [];
      setSelectedPositions(positionsArray);
      
      // Parse height
      if (initialData.height_inches) {
        const feet = Math.floor(initialData.height_inches / 12);
        const inches = Math.round(initialData.height_inches % 12);
        setHeightFeet(feet.toString());
        setHeightInches(inches.toString());
      } else {
        setHeightFeet('');
        setHeightInches('');
      }
      
      setWeight(initialData.weight_lbs ? initialData.weight_lbs.toString() : '');
      
      // Capitalize first letter for display
      setBats(initialData.bats ? initialData.bats.charAt(0).toUpperCase() + initialData.bats.slice(1).toLowerCase() : '');
      setThrows(initialData.throws ? initialData.throws.charAt(0).toUpperCase() + initialData.throws.slice(1).toLowerCase() : '');
      setAwards(initialData.awards || []);
      setHasChanges(false);
    }
  }, [isOpen, initialData]);

  // #189 — sanity ranges for the physical fields; blocks Save on garbage.
  const fieldErrors = useMemo(() => ({
    heightFeet: rangeError(heightFeet, RANGE_HEIGHT_FEET, { integer: true }),
    heightInches: rangeError(heightInches, RANGE_HEIGHT_INCHES, { integer: true }),
    weight: rangeError(weight, RANGE_WEIGHT_LBS, { integer: true }),
  }), [heightFeet, heightInches, weight]);

  const hasErrors = !hasNoErrors(fieldErrors);

  const handleSave = async () => {
    if (!sessionToken || !initialData?.id) return;

    // Validation
    if (!sport.trim()) {
      toast.warning('Please enter a sport');
      return;
    }

    if (selectedPositions.length === 0) {
      toast.warning('Please select at least one position');
      return;
    }

    // #189 / #203 — height + weight are now validated inline (see fieldErrors);
    // block Save on any range error instead of firing one-at-a-time toasts.
    if (hasErrors) {
      toast.error('Please fix the highlighted fields before saving.');
      return;
    }

    // Calculate total height in inches
    let totalHeightInches: number | undefined = undefined;
    if (heightFeet || heightInches) {
      const feet = parseInt(heightFeet) || 0;
      const inches = parseInt(heightInches) || 0;
      totalHeightInches = feet * 12 + inches;
    }

    const weightNum = weight ? parseInt(weight) : undefined;

    setIsSaving(true);

    try {
      const heightInches = totalHeightInches || 0;
      const weightLbs = weightNum ?? null;

      const payload = {
        sport: sport.trim(),
        positions: selectedPositions,
        bats: bats ? bats.toLowerCase() : '',
        throws: throws ? throws.toLowerCase() : '',
        height: totalHeightInches 
          ? `${Math.floor(totalHeightInches / 12)}'${totalHeightInches % 12}"` 
          : '',
        weight: weightLbs,
        awards: awards
      };

      const response = await athleteAPI.updateSportInfo(
        sessionToken,
        initialData.id,
        payload
      );

      if (response.success) {
        toast.success('Athletic info saved.');
        
        // Notify parent component of the update
        onUpdate({
          sport: sport.trim(),
          position: selectedPositions.join(', '),
          height_inches: heightInches,
          weight_lbs: weightLbs,
          bats: bats ? bats.toLowerCase() : undefined,
          throws: throws ? throws.toLowerCase() : undefined,
          awards: awards
        });

        (onSaved || onClose)();
      } else {
        toast.error('Failed to update athletic information. Please try again.');
      }
    } catch (error) {
      console.error('Error updating athletic information:', error);
      toast.error('Failed to update athletic information. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      inline={embedded}
      isDirty={hasChanges}
      labelledById="athletic-title"
      contentStyle={{
        background: 'white',
        borderRadius: '12px',
        maxWidth: isMobile ? 'calc(100vw - 32px)' : '600px',
        width: isMobile ? 'calc(100vw - 32px)' : '100%',
        maxHeight: isMobile ? 'calc(100vh - 100px)' : '90vh',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'hidden',
        fontFamily: 'Manrope, sans-serif',
      }}
    >
        {/* Header */}
        <div
          style={{
            padding: isMobile ? '16px' : '24px 28px',
            borderBottom: '1px solid #E5E7EB',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h2
            id="athletic-title"
            style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#1F2937',
              margin: 0,
            }}
          >
            Edit Athletic Information
          </h2>
          <button
            onClick={handleCancel}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              color: '#6B7280',
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Form - Scrollable */}
        <div style={{ padding: '28px', flex: 1, overflowY: 'auto' }}>
          {/* Sport */}
          <div style={{ marginBottom: '24px' }}>
            <label htmlFor="athletic-sport" style={athleticLabelStyle}>
              Sport *
            </label>
            <Select
              id="athletic-sport"
              value={sport}
              onChange={(e) => { setSport(e.target.value); setHasChanges(true); }}
              style={{
                ...athleticSelectStyle,
                /* Grey until something is picked, the way a placeholder reads. */
                color: sport ? '#1F2937' : '#9CA3AF',
              }}
            >
                <option value="" disabled>Select sport</option>
                <option value="Baseball">Baseball</option>
                <option value="Softball">Softball</option>
                <option value="Basketball">Basketball</option>
                <option value="Football">Football</option>
                <option value="Soccer">Soccer</option>
                <option value="Volleyball">Volleyball</option>
                <option value="Track & Field">Track & Field</option>
                <option value="Cross Country">Cross Country</option>
                <option value="Wrestling">Wrestling</option>
                <option value="Swimming">Swimming</option>
                <option value="Tennis">Tennis</option>
                <option value="Golf">Golf</option>
                <option value="Lacrosse">Lacrosse</option>
                <option value="Hockey">Hockey</option>
            </Select>
          </div>

          {/* Position */}
          <div style={{ marginBottom: '24px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '8px',
              }}
            >
              Position(s) *
            </label>
            <div
              role="button"
              tabIndex={0}
              aria-label="Select positions"
              onClick={() => { setPositionsSnapshot(selectedPositions); setShowPositionPicker(true); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setPositionsSnapshot(selectedPositions);
                  setShowPositionPicker(true);
                }
              }}
              style={{
                width: '100%',
                padding: '12px 14px',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                fontSize: '15px',
                fontFamily: 'Manrope, sans-serif',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                boxSizing: 'border-box',
                background: 'white',
                minHeight: '48px'
              }}
            >
              <span style={{ color: selectedPositions.length > 0 ? '#1F2937' : '#9CA3AF' }}>
                {selectedPositions.length > 0 ? selectedPositions.join(', ') : 'Select positions'}
              </span>
              <ChevronDown size={18} color="#6B7280" />
            </div>
          </div>

          {/* Height */}
          <div style={{ marginBottom: '24px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '8px',
              }}
            >
              Height
            </label>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <label htmlFor="athletic-height-feet" style={athleticSubLabelStyle}>
                  Feet
                </label>
                <TextInput
                  id="athletic-height-feet"
                  type="number"
                  value={heightFeet}
                  onChange={(e) => { setHeightFeet(e.target.value); setHasChanges(true); }}
                  placeholder="5"
                  min={RANGE_HEIGHT_FEET.min}
                  max={RANGE_HEIGHT_FEET.max}
                  invalid={!!fieldErrors.heightFeet}
                  aria-describedby={fieldErrors.heightFeet ? 'athletic-height-feet-error' : undefined}
                  style={athleticControlStyle}
                />
                {fieldErrors.heightFeet && <span id="athletic-height-feet-error" role="alert" style={athleticErrorStyle}>{fieldErrors.heightFeet}</span>}
              </div>
              <div style={{ flex: 1 }}>
                <label htmlFor="athletic-height-inches" style={athleticSubLabelStyle}>
                  Inches
                </label>
                <TextInput
                  id="athletic-height-inches"
                  type="number"
                  value={heightInches}
                  onChange={(e) => { setHeightInches(e.target.value); setHasChanges(true); }}
                  placeholder="8"
                  min={RANGE_HEIGHT_INCHES.min}
                  max={RANGE_HEIGHT_INCHES.max}
                  invalid={!!fieldErrors.heightInches}
                  aria-describedby={fieldErrors.heightInches ? 'athletic-height-inches-error' : undefined}
                  style={athleticControlStyle}
                />
                {fieldErrors.heightInches && <span id="athletic-height-inches-error" role="alert" style={athleticErrorStyle}>{fieldErrors.heightInches}</span>}
              </div>
            </div>
          </div>

          {/* Weight */}
          <div style={{ marginBottom: '24px' }}>
            <label htmlFor="athletic-weight" style={athleticLabelStyle}>
              Weight (lbs)
            </label>
            <TextInput
              id="athletic-weight"
              type="number"
              value={weight}
              onChange={(e) => { setWeight(e.target.value); setHasChanges(true); }}
              placeholder="140"
              min={RANGE_WEIGHT_LBS.min}
              max={RANGE_WEIGHT_LBS.max}
              invalid={!!fieldErrors.weight}
              aria-describedby={fieldErrors.weight ? 'athletic-weight-error' : undefined}
              style={athleticControlStyle}
            />
            {fieldErrors.weight && <span id="athletic-weight-error" role="alert" style={athleticErrorStyle}>{fieldErrors.weight}</span>}
          </div>

          {/* Bats */}
          <div style={{ marginBottom: '24px' }}>
            <label htmlFor="athletic-bats" style={athleticLabelStyle}>
              Bats
            </label>
            <Select
              id="athletic-bats"
              value={bats}
              onChange={(e) => { setBats(e.target.value); setHasChanges(true); }}
              style={{
                ...athleticSelectStyle,
                /* Grey until something is picked, the way a placeholder reads. */
                color: bats ? '#1F2937' : '#9CA3AF',
              }}
            >
                <option value="" disabled>Select batting hand</option>
                <option value="Right">Right</option>
                <option value="Left">Left</option>
                <option value="Switch">Switch</option>
            </Select>
          </div>

          {/* Throws */}
          <div style={{ marginBottom: '24px' }}>
            <label htmlFor="athletic-throws" style={athleticLabelStyle}>
              Throws
            </label>
            <Select
              id="athletic-throws"
              value={throws}
              onChange={(e) => { setThrows(e.target.value); setHasChanges(true); }}
              style={{
                ...athleticSelectStyle,
                /* Grey until something is picked, the way a placeholder reads. */
                color: throws ? '#1F2937' : '#9CA3AF',
              }}
            >
                <option value="" disabled>Select throwing hand</option>
                <option value="Right">Right</option>
                <option value="Left">Left</option>
            </Select>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '20px 28px',
            borderTop: '1px solid #E5E7EB',
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end',
          }}
        >
          <button
            onClick={handleCancel}
            disabled={isSaving}
            style={{
              padding: '12px 24px',
              fontSize: '15px',
              fontWeight: '600',
              color: '#374151',
              background: 'white',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              cursor: isSaving ? 'not-allowed' : 'pointer',
              fontFamily: 'Manrope, sans-serif',
              transition: 'all 0.2s ease',
              opacity: isSaving ? 0.5 : 1,
            }}
            onMouseEnter={(e) => {
              if (!isSaving) {
                e.currentTarget.style.background = '#F9FAFB';
                e.currentTarget.style.borderColor = '#9CA3AF';
              }
            }}
            onMouseLeave={(e) => {
              if (!isSaving) {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.borderColor = '#D1D5DB';
              }
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || hasErrors}
            style={{
              padding: '12px 28px',
              fontSize: '15px',
              fontWeight: '600',
              color: 'white',
              background: (isSaving || hasErrors) ? '#9CA3AF' : '#F9550D',
              border: 'none',
              borderRadius: '8px',
              cursor: (isSaving || hasErrors) ? 'not-allowed' : 'pointer',
              fontFamily: 'Manrope, sans-serif',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              if (!isSaving) {
                e.currentTarget.style.background = '#E04A0A';
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(249, 85, 13, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isSaving) {
                e.currentTarget.style.background = '#F9550D';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }
            }}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
    </Modal>

      {/* Position Picker Modal */}
      {showPositionPicker && (
        <div
          onClick={() => { setSelectedPositions(positionsSnapshot); setShowPositionPicker(false); }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10010
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '90%',
              maxWidth: '500px',
              maxHeight: '80vh',
              background: 'white',
              borderRadius: '16px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px 24px',
              borderBottom: '1px solid #E5E7EB'
            }}>
              <button
                onClick={() => { setSelectedPositions(positionsSnapshot); setShowPositionPicker(false); }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '16px',
                  color: '#6B7280',
                  cursor: 'pointer',
                  fontFamily: 'Manrope, sans-serif'
                }}
              >
                Cancel
              </button>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1F2937',
                margin: 0,
                fontFamily: 'Manrope, sans-serif'
              }}>
                Select Positions
              </h3>
              <button
                onClick={() => {
                  if (JSON.stringify(selectedPositions) !== JSON.stringify(positionsSnapshot)) {
                    setHasChanges(true);
                  }
                  setShowPositionPicker(false);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#F9550D',
                  cursor: 'pointer',
                  fontFamily: 'Manrope, sans-serif'
                }}
              >
                Done ({selectedPositions.length})
              </button>
            </div>

            {/* Modal Body */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '8px 24px'
            }}>
              {availablePositions.map((pos) => (
                <div
                  key={pos}
                  onClick={() => {
                    setSelectedPositions(prev =>
                      prev.includes(pos)
                        ? prev.filter(p => p !== pos)
                        : [...prev, pos]
                    );
                  }}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '16px 12px',
                    borderBottom: '1px solid #F3F4F6',
                    background: selectedPositions.includes(pos) ? 'rgba(249, 85, 13, 0.1)' : 'white',
                    cursor: 'pointer',
                    fontFamily: 'Manrope, sans-serif',
                    borderRadius: '8px',
                    marginBottom: '4px'
                  }}
                >
                  <span style={{
                    fontSize: '16px',
                    color: '#1F2937',
                    fontWeight: selectedPositions.includes(pos) ? '600' : '400'
                  }}>
                    {pos}
                  </span>
                  {selectedPositions.includes(pos) && (
                    <div style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '12px',
                      background: '#F9550D',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M11.6667 3.5L5.25 9.91667L2.33333 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AthleticInfoEditModal;
